import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
} from '@kollektive/design-system';
import { AlertTriangle } from 'lucide-react';
import type { Invitation } from '@/hooks/useInvitations';
import styles from './CancelInvitationDialog.module.css';

interface CancelInvitationDialogProps {
  invitation: Invitation;
  invitationCount?: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

export function CancelInvitationDialog({
  invitation,
  invitationCount = 1,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: CancelInvitationDialogProps) {
  const isBulk = invitationCount > 1;

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <ModalBody>
        <div className={styles.content}>
          {/* Warning icon */}
          <div className={styles.iconWrapper}>
            <AlertTriangle size={24} className={styles.warningIcon} />
          </div>

          <h2 className={styles.title}>
            {isBulk ? `Cancel ${invitationCount} Invitations` : 'Cancel Invitation'}
          </h2>

          <p className={styles.description}>
            {isBulk ? (
              <>
                Are you sure you want to cancel <strong>{invitationCount} invitations</strong>?
                The recipients will no longer be able to join your workspace using these invites.
              </>
            ) : (
              <>
                Are you sure you want to cancel the invitation for <strong>{invitation.email}</strong>?
                They will no longer be able to join your workspace using this invite.
              </>
            )}
          </p>

          {/* Invitation info - only show for single invitation */}
          {!isBulk && (
            <Card>
              <CardBody>
                <div className={styles.invitationInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Email</span>
                    <span className={styles.infoValue}>{invitation.email}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Role</span>
                    <span className={styles.infoValue}>{invitation.role}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Invited by</span>
                    <span className={styles.infoValue}>{invitation.invited_by.name}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isLoading}
        >
          Keep Invitation
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading
            ? 'Cancelling...'
            : isBulk
              ? `Cancel ${invitationCount} Invitations`
              : 'Cancel Invitation'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
